import React, { useState, useCallback, useRef } from 'react';
import { PageWrapper } from '../components/partials';
import Reports from '../services/reports';
import { useUrlParams } from '../utils/useUrlParamsHook';
import { expandMetadata, expandRow } from '../utils/reports';
import css from './page.css';
import Filters from './components/filters/Filters';
import Sunburst from './components/sunburst/Sunburst';
import {
    buildRingQueryParams,
    getMetricColumnKey,
    getMetricDrilldownFields,
    buildRingPoints,
    buildQueryKey,
    assignGlobalColors,
} from './queryUtils';
import { ANOMALY_EVENTS, ChartColorConfig, DimensionConfig, DIMENSION_SEPARATOR, MAX_CACHE_ENTRIES, formatChangeValue, trackAnomalyEvent } from './consts';
import { IN_OPERATOR } from '../utils/reportsConsts';
import DrilldownView from './components/DrilldownView';
import { CONTEXT_PROD_MODE } from '../chatView/constants';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getPeriodDays = period => {
    if (!period?.start || !period?.end) return null;
    const ms = new Date(period.end) - new Date(period.start);
    return Number.isFinite(ms) ? Math.round(ms / MS_PER_DAY) + 1 : null;
};

const countActiveFilters = filters => (filters || []).filter(f => f.values?.length).length;

const buildSegmentFilters = (pathParts, dimensions) =>
    dimensions.map((dim, i) => {
        if (i >= pathParts.length) return null;
        const config = DimensionConfig[dim.value || dim];
        if (!config) return null;
        return { dimension: config.apiName, operator: IN_OPERATOR, values: [pathParts[i]] };
    }).filter(Boolean);

const normalizeSidebarFilters = filters =>
    (filters || [])
        .filter(f => f.values?.length)
        .map(f => ({ dimension: DimensionConfig[f.type]?.apiName, operator: IN_OPERATOR, values: f.values.map(v => v.value) }));

const mergeDrilldownFilters = (segmentFilters, sidebarFilters) => {
    const segmentDimensions = new Set(segmentFilters.map(f => f.dimension));
    return [
        ...segmentFilters,
        ...sidebarFilters.filter(f => !segmentDimensions.has(f.dimension)),
    ];
};

const buildDrilldownPayload = (queryResult, pointData) => {
    const pathParts = pointData.id.split(DIMENSION_SEPARATOR);
    const segmentFilters = buildSegmentFilters(pathParts, queryResult.dimensions || []);
    const sidebarFilters = normalizeSidebarFilters(queryResult.filters);
    return {
        metric: {
            api_name: queryResult.colorMetricApiName || '',
            display_name: queryResult.metricLabels?.color || '',
        },
        cohort_period: queryResult.colorMetricCohortPeriod || null,
        filters: mergeDrilldownFilters(segmentFilters, sidebarFilters),
        absolute_change: pointData.custom?.rawColorChange,
        pct_change: pointData.custom?.rawColorChangePct,
        start_date: queryResult.firstPeriod?.start || '',
        end_date: queryResult.firstPeriod?.end || '',
        previous_period_start: queryResult.secondPeriod?.start || '',
        previous_period_end: queryResult.secondPeriod?.end || '',
    };
};

const reportsService = new Reports();

const expandReportData = reportData => {
    const metadata = expandMetadata(reportData.metadata, true);
    const rows = (reportData.results || []).map(row => expandRow(metadata, row));
    return { rows, metadata };
};

const findMetricMeta = (rawMetadata, columnKey) => {
    const baseName = columnKey?.split('__')[0];
    const entry = rawMetadata[baseName];
    if (!entry) return null;
    if (!entry.columns) return entry;
    const period = columnKey.split('__').slice(1).join('__');
    const subColumn = entry.columns.find(col => col.name === period);
    return subColumn ? { ...entry, display_name: subColumn.display_name } : entry;
};

const extractMetricsMeta = (rawMetadata, colorKey, sizeKey) => ({
    colorMeta: findMetricMeta(rawMetadata, colorKey),
    sizeMeta: findMetricMeta(rawMetadata, sizeKey),
});

const getMetricLabel = meta => meta?.display_name || meta?.name?.toUpperCase() || '';

const resolveChartColors = increasesAreGood => ({
    positive: increasesAreGood ? ChartColorConfig.GOOD : ChartColorConfig.BAD,
    negative: increasesAreGood ? ChartColorConfig.BAD : ChartColorConfig.GOOD,
    neutral: ChartColorConfig.NEUTRAL,
    noData: ChartColorConfig.NO_DATA,
});

const AnomalyAnalysisPage = () => {
    const { getParamsObject, setParamsObject } = useUrlParams();
    const [initialUrlParams, setInitialUrlParams] = useState(getParamsObject);
    const filterParamsRef = useRef(initialUrlParams);
    const [queryResult, setQueryResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [drilldownState, setDrilldownState] = useState(null);
    const [contextEnv, setContextEnv] = useState(CONTEXT_PROD_MODE);

    const latestRunRef = useRef(0);
    const cacheRef = useRef(new Map());

    const buildResult = (reportDataList, paramsList, queryConfig) => {
        const colorKey = getMetricColumnKey(queryConfig.colorMetric.value);
        const sizeKey = getMetricColumnKey(queryConfig.sizeMetric.value);

        const expandedRings = reportDataList.map(expandReportData);
        const rawMetadata = reportDataList[0].metadata || {};
        const { colorMeta, sizeMeta } = extractMetricsMeta(rawMetadata, colorKey, sizeKey);
        const metaIncreasesAreGood = colorMeta?.increases_are_good ?? true;

        const rawPoints = expandedRings.flatMap(({ rows }, ringLevel) => {
            const apiDimKeys = paramsList[ringLevel].dimensions;
            return buildRingPoints(rows, apiDimKeys, sizeKey, colorKey, ringLevel);
        });
        const allPoints = assignGlobalColors(rawPoints, metaIncreasesAreGood);

        const colorDrilldownFields = getMetricDrilldownFields(queryConfig.colorMetric.value);

        return {
            data: allPoints,
            chartColors: resolveChartColors(metaIncreasesAreGood),
            colorMetricApiName: colorDrilldownFields.api_name,
            colorMetricCohortPeriod: colorDrilldownFields.cohort_period,
            firstPeriod: queryConfig.firstPeriod,
            secondPeriod: queryConfig.secondPeriod,
            dimensions: queryConfig.dimensions,
            filters: queryConfig.filters,
            increasesAreGood: metaIncreasesAreGood,
            metricLabels: {
                color: getMetricLabel(colorMeta),
                size: getMetricLabel(sizeMeta),
            },
            displayFormats: {
                color: colorMeta?.display_format || null,
                size: sizeMeta?.display_format || null,
            },
        };
    };

    const handleRun = useCallback(async queryConfig => {
        const params = {
            dimensions: queryConfig.dimensions.map(d => d.value),
            colorMetric: queryConfig.colorMetric?.value,
            sizeMetric: queryConfig.sizeMetric?.value,
            filters: queryConfig.filters,
            firstPeriod: queryConfig.firstPeriod,
        };
        filterParamsRef.current = params;
        setParamsObject(params);

        const runId = ++latestRunRef.current;
        const key = buildQueryKey(queryConfig);
        const cached = cacheRef.current.get(key);

        const runProps = {
            dimensions: params.dimensions,
            dimensionCount: params.dimensions.length,
            colorMetric: params.colorMetric,
            sizeMetric: params.sizeMetric,
            periodDays: getPeriodDays(queryConfig.firstPeriod),
            filterCount: countActiveFilters(queryConfig.filters),
        };
        trackAnomalyEvent(ANOMALY_EVENTS.RUN_QUERY, { ...runProps, fromCache: !!cached });

        if (cached) {
            setIsLoading(false);
            setError(false);
            setQueryResult(buildResult(cached.reportDataList, cached.paramsList, queryConfig));
            return;
        }
        setIsLoading(true);
        setError(false);
        try {
            const paramsList = buildRingQueryParams(queryConfig);
            const reportDataList = await Promise.all(
                paramsList.map((params, i) => reportsService.runQuery(params, { suffix: `ring${i}` }))
            );
            if (runId !== latestRunRef.current) return;

            if (cacheRef.current.size >= MAX_CACHE_ENTRIES) {
                cacheRef.current.delete(cacheRef.current.keys().next().value);
            }
            cacheRef.current.set(key, { reportDataList, paramsList });
            setQueryResult(buildResult(reportDataList, paramsList, queryConfig));
        } catch (err) {
            if (runId !== latestRunRef.current) return;
            setError(true);
            trackAnomalyEvent(ANOMALY_EVENTS.RUN_FAILED, runProps);
        } finally {
            if (runId === latestRunRef.current) setIsLoading(false);
        }
    }, []);

    const handleDrilldown = useCallback(pointData => {
        if (!queryResult) return;
        const payload = buildDrilldownPayload(queryResult, pointData);
        trackAnomalyEvent(ANOMALY_EVENTS.DRILLDOWN_RUN, {
            metric: payload.metric?.api_name,
            pctChange: payload.pct_change,
            depth: pointData.id.split(DIMENSION_SEPARATOR).length,
            filterCount: payload.filters.length,
        });
        setDrilldownState(payload);
    }, [queryResult]);

    return (
        <PageWrapper className={css.pageWrapper} disablePadding>
            <div className={css.body}>
                {drilldownState ? (
                    <DrilldownView
                        payload={drilldownState}
                        contextEnv={contextEnv}
                        onContextEnvChange={setContextEnv}
                        onBack={() => {
                            trackAnomalyEvent(ANOMALY_EVENTS.DRILLDOWN_CLOSE, {});
                            setInitialUrlParams(filterParamsRef.current);
                            setDrilldownState(null);
                        }}
                    />
                ) : (
                    <>
                        <Filters onRun={handleRun} initialUrlParams={initialUrlParams} />
                        <div className={css.content}>
                            <Sunburst
                                data={queryResult?.data}
                                isLoading={isLoading}
                                error={error}
                                hasQueried={!!queryResult}
                                chartColors={queryResult?.chartColors}
                                metricLabels={queryResult?.metricLabels}
                                displayFormats={queryResult?.displayFormats}
                                increasesAreGood={queryResult?.increasesAreGood}
                                onDrilldown={handleDrilldown}
                                firstPeriod={queryResult?.firstPeriod}
                                secondPeriod={queryResult?.secondPeriod}
                                contextEnv={contextEnv}
                                onContextEnvChange={setContextEnv}
                            />
                        </div>
                    </>
                )}
            </div>
        </PageWrapper>
    );
};

export default AnomalyAnalysisPage;
